import React, { useEffect } from 'react';

import { BreadCrumbNavigation, StageBreadCrumb } from 'components/BreadCrumbNavigation/BreadCrumbNavigation';
import type { OrderStageOption } from 'pages/partners/components/OrdersTab/OrdersTab';
import { GMetrixCheckInput } from 'components/GMetrixCheckInput/GMetrixCheckInput';
import { GMetrixDatePicker } from 'components/GMetrixDatePicker/GMetrixDatePicker';
import { Order, OrderApi, OrderStatus, OrderUseRecord } from 'apis/OrderApi';
import { GMetrixButton } from 'components/GMetrixButton/GMetrixButton';
import { TileContainer } from 'components/TileContainer/TileContainer';
import { GMetrixInput } from 'components/GMetrixInput/GMetrixInput';
import { getFormattedDate, getFormattedDateApi } from 'utils';
import { FormattedMessage, useIntl } from 'react-intl';
import { usePartners } from 'pages/partners/context';
import { useNotification } from 'app/notifications';
import { Col, Row } from 'react-bootstrap';
import { GMetrixTable } from 'components';
import { useSuperState } from 'hooks';

import 'pages/partners/components/OrderDetails/styles/order-details.scss';

interface OrderDetailsProps {
	breadCrumbs: StageBreadCrumb<OrderStageOption>[];
    getRenewalOrderDetailsClick: (orderId: number) => void;
    handleBreadCrumbClick: (currentBreadCrumb: StageBreadCrumb<OrderStageOption>) => void
    orderDetails: Order;
    renewedOrder: boolean;
    renewOrRetractOrderDetails: (orderId: number, renewOrRetract: 'Renew' | 'Retract') => void
    resetBackToLookup: () => void;
    updateOrderDetailsTemp: (order: Order) => void
}

export const OrderDetails = ({
    breadCrumbs,
    getRenewalOrderDetailsClick,
    handleBreadCrumbClick,
    orderDetails,
    renewedOrder,
    renewOrRetractOrderDetails,
    resetBackToLookup,
    updateOrderDetailsTemp
}: OrderDetailsProps) => {

    const { formatMessage } = useIntl();
    const [state, setState] = useSuperState({
        ...orderDetails,
        commentIsValid: true,
        disabledSaveBtn: true,
        loadingOrderUseRecords: true,
        newComment: '',
        orderUseRecords: [] as OrderUseRecord[]
    });
    const { commentIsValid, disabledSaveBtn, loadingOrderUseRecords, newComment, orderUseRecords } = state;
    const tempOrderState = state;
    const setTempOrderState = setState;
    const { permissions } = usePartners();
    const { catchWithToast, createToast } = useNotification();

    const filledByPartnerName = orderDetails.FilledByPartnerName?.trim();
    const fulfilledByValue = filledByPartnerName && filledByPartnerName.toLowerCase() !== 'unknown'
        ? filledByPartnerName
        : orderDetails.OrderedBy?.trim() || formatMessage({ id: 'unknown.3fe48b83', defaultMessage: 'Unknown' });

    useEffect(() => {

        setState({ loadingOrderUseRecords: true, orderUseRecords: [] });
        getOrderUseRecords();
        if (tempOrderState.ID !== orderDetails.ID) {

            setTempOrderState(orderDetails);

        }

    }, [orderDetails.Id]);

    const getOrderUseRecords = async () => {

        if (orderDetails.OrderStatus !== OrderStatus.Pending) {

            try {

                const val = await OrderApi.GetOrderUseRecords(orderDetails.Id, 1);
                setState({ orderUseRecords: val });

            } catch (e) {

                catchWithToast(formatMessage({ id: 'failed_to_load_order_use_records.77762d97', defaultMessage: 'Failed to load order use records.' }))(e);

            } finally {

                setState({ loadingOrderUseRecords: false });

            }

        }

    };

    const handleSaveOrderDetails = async () => {

        let hasError = false;
        let errorMessage = '';

        if (orderDetails.DateStart !== tempOrderState.DateStart || orderDetails.DateExpire !== tempOrderState.DateExpire || newComment.length > 0) {

            if (newComment.length > 0) {

                const startDifference = (new Date(tempOrderState.DateStart.toString()).getTime() - new Date(orderDetails.DateStart.toString()).getTime()) / (1000 * 60 * 60 * 24);
                let expirationDifference = 0;
                if (tempOrderState.DateExpire && orderDetails.DateExpire) {

                    expirationDifference = (new Date(tempOrderState.DateExpire.toString()).getTime() - new Date(orderDetails.DateExpire.toString()).getTime()) / (1000 * 60 * 60 * 24);

                }
                setTempOrderState({
                    Comment: `${newComment}|${tempOrderState.Comment}`
                });

                OrderApi.Update(orderDetails.Id, newComment, startDifference, expirationDifference).catch(catchWithToast(formatMessage({ id: 'failed_to_update_order.13bfa7e5', defaultMessage: 'Failed to update order.' })));

            } else {

                createToast(formatMessage({ id: 'please_add_a_comment_when_updating_the_s.965c6a28', defaultMessage: "Please add a comment when updating the 'Start Date' and 'Expiration Date' fields." }), 'danger');
                setState({ commentIsValid: false });
                return;

            }

        }

        if (orderDetails.PurchaseOrder !== tempOrderState.PurchaseOrder) {

            try {

                const response = await OrderApi.UpdatePurchaseOrder(orderDetails.Id, tempOrderState.PurchaseOrder);
                if (!response.Done) {

                    hasError = true;
                    errorMessage = response.Message;

                }

            } catch (e) {

                catchWithToast(formatMessage({ id: 'failed_to_update_purchase_order.97be0545', defaultMessage: 'Failed to update purchase order.' }))(e);

            }

        }

        if (hasError) {

            createToast(errorMessage, 'danger');

        } else {

            updateOrderDetailsTemp(tempOrderState);
            createToast(formatMessage({ id: 'successfully_changed_order_details.59c1c65a', defaultMessage: 'Successfully changed Order Details.' }), 'success');

            resetBackToLookup();

        }

    };

    return (
        <>
            <BreadCrumbNavigation breadCrumbs={breadCrumbs} handleBreadCrumbClick={handleBreadCrumbClick} />
            <TileContainer
                addButton
                addButtonCustom={(
                    <>
                        {orderDetails.OrderStatus !== OrderStatus.Canceled && !renewedOrder && (
                            <GMetrixButton disabled={!permissions.Edit || disabledSaveBtn} iconClass="fa-save fa-solid" onClick={handleSaveOrderDetails} variant="primary">
                                <FormattedMessage defaultMessage="Save" id="save.d44082ae" />
                            </GMetrixButton>
                        )}
                    </>
                )}
            >
                <Row>
                    <Col className="col-12 my-2" lg={4} md={6}>
                        <GMetrixInput readOnly label={formatMessage({ id: 'license_name.8773f362', defaultMessage: 'License Name' })} value={orderDetails.License.Name} />
                    </Col>
                    <Col className="col-12 my-2" lg={4} md={6}>
                        <GMetrixInput readOnly label={formatMessage({ id: 'order_id.ab576b3d', defaultMessage: 'Order ID', description: 'Formulario' })} value={orderDetails.OrderControl} />
                    </Col>
                    <Col className="col-12 my-2" lg={4} md={6}>
                        <GMetrixInput readOnly label={formatMessage({ id: 'status.ad856ad7', defaultMessage: 'Status' })} value={orderDetails.OrderStatusText} />
                    </Col>
                </Row>
                <Row>
                    <Col className="col-12 my-2" lg={4} md={6}>
                        <GMetrixInput
                            readOnly
                            label={formatMessage({ id: 'date_created.85efe6f3', defaultMessage: 'Date Created' })}
                            value={getFormattedDateApi(orderDetails.DateCreate)}
                        />
                    </Col>
                    <Col className="col-12 my-2" lg={4} md={6}>
                        <GMetrixInput readOnly label={formatMessage({ id: 'ordered_by.381902ec', defaultMessage: 'Ordered By', description: 'Formulario' })} value={orderDetails.OrderedBy} />
                    </Col>
                    <Col className="col-12 my-2" lg={4} md={6}>
                        <GMetrixInput readOnly label={formatMessage({ id: 'fulfilled_by.46a45651', defaultMessage: 'Fulfilled By' })} value={fulfilledByValue} />
                    </Col>
                    <Col className="col-12 my-2" lg={4} md={6}>
                        <GMetrixInput
                            onChange={e => {

                                setState({ disabledSaveBtn: false });
                                setTempOrderState({
                                    PurchaseOrder: e.target.value
                                });

                            }}
                            label={formatMessage({ id: 'purchased_order.7f3e166e', defaultMessage: 'Purchased Order' })}
                            readOnly={orderDetails.OrderStatus === OrderStatus.Canceled || !permissions.Edit || renewedOrder}
                            value={tempOrderState.PurchaseOrder}
                        />
                    </Col>
                    <Col className="col-12 my-2" lg={4} md={6}>
                        <GMetrixDatePicker
                            isValid
                            onChange={(date: any) => {

                                if (date) {

                                    setTempOrderState({
                                        DateStart: getFormattedDate(new Date(date))
                                    });
                                    setState({ disabledSaveBtn: false });

                                }

                            }
                            }
                            label={formatMessage({ id: 'start_date.66ba5b72', defaultMessage: 'Start Date' })}
                            min={new Date().toISOString().split('T')[0]}
                            readOnly={orderDetails.OrderStatus === OrderStatus.Canceled || !permissions.Edit || renewedOrder}
                            value={new Date(tempOrderState.DateStart)}
                        />
                    </Col>
                    <Col className="col-12 my-2" lg={4} md={6}>
                        <GMetrixDatePicker
                            isValid
                            onChange={(date: any) => {

                                if (date) {

                                    setTempOrderState({
                                        DateExpire: getFormattedDate(new Date(date))
                                    });
                                    setState({ disabledSaveBtn: false });

                                }

                            }
                            }
                            label={formatMessage({ id: 'expiration_date.7e4bebd2', defaultMessage: 'Expiration Date' })}
                            min={new Date().toISOString().split('T')[0]}
                            readOnly={orderDetails.OrderStatus !== OrderStatus.Processed || !permissions.AdvancedEdit || renewedOrder}
                            value={orderDetails.OrderStatus === OrderStatus.Pending ? undefined : new Date(tempOrderState.DateExpire)}
                        />
                    </Col>
                    {permissions.AdvancedEdit && orderDetails.License.AllowOffline && (
                    <Col className="col-12 my-2" lg={4} md={6}>
                        <GMetrixCheckInput
                            onChange={e => {

                                setState({ disabledSaveBtn: false });
                                setTempOrderState({
                                    IsOffline: e.target.checked
                                });

                            }}
                            checked={tempOrderState.IsOffline}
                            className="mt-4"
                            disabled={orderDetails.OrderStatus === OrderStatus.Canceled || renewedOrder}
                            label={formatMessage({ id: 'offline.94575a93', defaultMessage: 'Offline' })}
                            type={'checkbox'}
                            value={tempOrderState.IsOffline}
                        />
                    </Col>
                    )}
                </Row>
                <Row>
                    <Col className="col-12 my-2" md={6}>
                        <GMetrixInput
                            onChange={e => {

                                setState({ disabledSaveBtn: false, newComment: e.target.value.toString() });

                            }}
                            label={formatMessage({ id: 'comments.bfa0c2a5', defaultMessage: 'Comments' })}
                            onBlur={() => setState({ commentIsValid: true })}
                            readOnly={orderDetails.OrderStatus === OrderStatus.Canceled || !permissions.Edit || renewedOrder}
                            type="textarea"
                            value={newComment}
                        />
                    </Col>
                    <Col className="col-12 my-2" md={6}>
                        <div className="order-details__past-comments">

                            <FormattedMessage defaultMessage="Past Comments" id="past_comments.cf923217" />
                        </div>
                        <div className="order-details__past-comments-container px-2">
                            {tempOrderState.Comment && tempOrderState.Comment.length > 0 ?
                                tempOrderState.Comment.split('|').map((comment, i) => (
                                    <div className="my-1" key={`${comment}_${i}`}>
                                        {comment}
                                    </div>
                                )) : (
                                    <div className="my-1">

                                        <FormattedMessage defaultMessage="No comments have been saved" id="no_comments_have_been_saved.f7c8e514" />
                                    </div>
                                )
                            }
                        </div>
                    </Col>
                </Row>
                <Row>
                    <Col className="my-4 col-12">
                        <div className="order-details__license-header">
                            <FormattedMessage defaultMessage="Renewal Chain" id="renewal_chain.d9f9ecd3" />
                        </div>
                    </Col>
                    <Col className="mb-3 col-12 d-flex align-items-center" md={6}>
                        {orderDetails.PreviousId !== -1 && (
                            <>
                                <div className="mx-2">
                                    <div>
                                        <FormattedMessage defaultMessage="Previous Order" id="previous_order.0d44dee4" />
                                    </div>
                                    <a className="order-details__renewal_link" onClick={() => getRenewalOrderDetailsClick(orderDetails.PreviousId)}>
                                        {orderDetails.PreviousControl}
                                    </a>
                                </div>
                                <div className="mx-2 d-flex align-items-center">
                                    <i className="order-details__chevron fa-solid fa-chevron-right"></i>
                                </div>
                            </>
                        )}
                        <div className="mx-2">
                            <div>
                                <FormattedMessage defaultMessage="This Order" id="this_order.a2a3e8d6" />
                            </div>
                            <a>
                                {orderDetails.OrderControl}
                            </a>
                        </div>
                        {
                            renewedOrder && (
                                <>
                                    <div className="mx-2 d-flex align-items-center">
                                        <i className="order-details__chevron fa-solid fa-chevron-right"></i>
                                    </div>
                                    <div className="mx-2">
                                        <div>
                                            <FormattedMessage defaultMessage="Renewed By" id="renewed_by.ab5b1f75" />
                                        </div>
                                        <a className="order-details__renewal_link" onClick={() => getRenewalOrderDetailsClick(orderDetails.RenewId)}>
                                            {orderDetails.RenewedControl}
                                        </a>
                                    </div>
                                </>
                            )
                        }
                    </Col>
                    {!renewedOrder && orderDetails.OrderStatus === OrderStatus.Processed && (
                    <Col className="mb-3 col-12 d-flex justify-content-end" md={6}>
                        {permissions.PlaceAnOrder && (
                        <GMetrixButton className="m-2" disabled={!permissions.Edit} onClick={() => renewOrRetractOrderDetails(orderDetails.Id, 'Renew')} variant="secondary">
                            <FormattedMessage defaultMessage="Renew Order" id="renew_order.e768c154" />
                        </GMetrixButton>
                        )}
                        <GMetrixButton className="ml-2 mb-2 mt-2" disabled={!permissions.Edit} onClick={() => renewOrRetractOrderDetails(orderDetails.Id, 'Retract')} variant="danger">
                            <FormattedMessage defaultMessage="Retract Order" id="retract_order.c8f1bea1" />
                        </GMetrixButton>
                    </Col>
                    )}
                    {orderDetails.OrderStatus !== OrderStatus.Pending && (
                    <GMetrixTable
                        allowExport
                        columnData={[{ field: 'DeliverableTypeName', title: formatMessage({ id: 'deliverable_type.2d29260f', defaultMessage: 'Deliverable Type' }) },
                            { field: 'MaxUses', title: formatMessage({ id: 'license_max_uses.90a05d96', defaultMessage: 'License Max Uses' }) },
                            { field: 'TotalRedemptions', title: formatMessage({ id: 'total_redemptions.1e0874fb', defaultMessage: 'Total Redemptions' }) },
                            { field: 'NumGranted', title: formatMessage({ id: 'licenses_ordered.7d35c82a', defaultMessage: 'Licenses Ordered' }) },
                            { field: 'UniqueUsers', title: formatMessage({ id: 'unique_users.54acf9c7', defaultMessage: 'Unique Users' }) },
                            { cell: data => {

                                const { DeliverableTypeId, DeliverableTypeName, MaxUses, OrderId, TotalRedemptions, UsesRemaining } = data.dataItem;
                                return (
                                    <td key={`useRecords_${DeliverableTypeId}`}>
                                        {MaxUses === 'Unlimited' && TotalRedemptions === 'Unlimited' ? 'Unlimited' : (
                                            <GMetrixInput
                                                onBlur={e => {

                                                    OrderApi.UpdateUsesRemainingRecord(OrderId, DeliverableTypeId, DeliverableTypeName, parseInt(e.target.value), 1);

                                                }}
                                                defaultValue={UsesRemaining.toString()}
                                                disabled={orderDetails.OrderStatus === OrderStatus.Canceled && !permissions.AdvancedEdit || renewedOrder}
                                            />
                                        )}

                                    </td>
                                );

                            }, field: 'UsesRemaining',
                            title: formatMessage({ id: 'uses_remaining.d26b9e27', defaultMessage: 'Uses Remaining' }),
                            width: '110px' }]}
                        className="my-4"
                        loading={loadingOrderUseRecords}
                        rowData={orderUseRecords}
                    />
                    )}
                </Row>
            </TileContainer>
        </>
    );

};
